import { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Plus, Minus, X, Check, ArrowLeft, StickyNote, Wine, Grape, Coffee, Banknote, QrCode, CreditCard, DoorOpen, DoorClosed, Sparkles, Download } from "lucide-react";
import { downloadReceipt } from "@/lib/receipt";

function PaymentModal({ order, onClose, onPaid }) {
    const subtotal = order.subtotal || 0;
    const paidSoFar = (order.payments || []).reduce((s, p) => s + p.amount, 0);
    const remaining = subtotal - paidSoFar;
    const [method, setMethod] = useState("cash");
    const [parts, setParts] = useState(1);
    const [customAmount, setCustomAmount] = useState("");
    const [busy, setBusy] = useState(false);

    const perPart = Math.ceil(remaining / parts);
    const useCustom = customAmount && !isNaN(+customAmount);
    const amount = useCustom ? Math.min(+customAmount, remaining) : perPart;

    const submit = async () => {
        setBusy(true);
        try {
            const r = await api.post(`/orders/${order.id}/pay`, { method, amount });
            const newRemaining = subtotal - (r.data.payments || []).reduce((s, p) => s + p.amount, 0);
            if (newRemaining > 0) {
                toast.success(`Zaplaceno ${amount} Kč · zbývá ${newRemaining} Kč`);
                onPaid(r.data);
            } else {
                toast.success(`Stůl ${order.table_label} zaplacen (${subtotal} Kč)`);
                onPaid(r.data, true);
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const qrData = encodeURIComponent(`SPD*1.0*ACC:CZ0000000000000000000000*AM:${amount}.00*CC:CZK*MSG:Strokay stul ${order.table_label}`);
    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${qrData}`;

    return (
        <div className="fixed inset-0 z-[60] bg-burgundyDeep/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="payment-modal">
            <div className="w-full max-w-lg bg-cream p-8 border border-ink/15 max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <p className="overline">Placení · Stůl {order.table_label}</p>
                        <div className="font-serif text-5xl text-ink mt-2">{remaining} <span className="text-2xl text-ink2">Kč</span></div>
                        <p className="text-xs text-ink2 mt-1">Zbývá zaplatit (celkem {subtotal} Kč{paidSoFar > 0 ? `, zaplaceno ${paidSoFar}` : ""})</p>
                    </div>
                    <button onClick={onClose} className="w-10 h-10 flex items-center justify-center hover:bg-cream2" data-testid="payment-close"><X size={18} /></button>
                </div>

                {/* Split */}
                <div className="mb-6">
                    <p className="overline mb-2">Rozdělení účtu</p>
                    <div className="flex gap-2">
                        {[1, 2, 3, 4].map((n) => (
                            <button key={n} data-testid={`pay-split-${n}`}
                                onClick={() => { setParts(n); setCustomAmount(""); }}
                                className={`flex-1 py-2.5 text-xs uppercase tracking-[0.18em] border transition-all ${parts === n && !useCustom
                                    ? "bg-burgundy text-cream border-burgundy" : "bg-transparent border-ink/15 hover:border-burgundy"}`}>
                                {n === 1 ? "Celé" : `${n}× ${Math.ceil(remaining / n)} Kč`}
                            </button>
                        ))}
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                        <span className="overline shrink-0">Nebo</span>
                        <input type="number" value={customAmount} placeholder="vlastní částka"
                            data-testid="pay-custom-amount"
                            onChange={(e) => setCustomAmount(e.target.value)}
                            className="flex-1 p-2 border border-ink/15 bg-cream2 text-sm" />
                        <span className="text-xs text-ink2">Kč</span>
                    </div>
                </div>

                {/* Method */}
                <div className="mb-6">
                    <p className="overline mb-2">Způsob platby</p>
                    <div className="grid grid-cols-3 gap-2">
                        {[
                            { k: "cash", l: "Hotově", I: Banknote },
                            { k: "qr", l: "QR", I: QrCode },
                            { k: "card", l: "Karta", I: CreditCard },
                        ].map((m) => {
                            const Icon = m.I;
                            return (
                                <button key={m.k} data-testid={`pay-method-${m.k}`} onClick={() => setMethod(m.k)}
                                    className={`flex flex-col items-center gap-2 py-4 border transition-all ${method === m.k
                                        ? "bg-burgundy text-cream border-burgundy" : "bg-transparent border-ink/15 hover:border-burgundy"}`}>
                                    <Icon size={22} />
                                    <span className="text-[11px] uppercase tracking-[0.18em] font-semibold">{m.l}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {method === "qr" && (
                    <div className="mb-6 flex flex-col items-center p-4 bg-cream2 border border-ink/10" data-testid="pay-qr-code">
                        <img src={qrUrl} alt="QR platba" className="mb-2" width={200} height={200} />
                        <p className="text-xs text-ink2 text-center">Host naskenuje QR a zaplatí {amount} Kč</p>
                    </div>
                )}

                <button onClick={submit} disabled={busy || amount <= 0} data-testid="pay-confirm"
                    className="btn-burgundy w-full">
                    <Check size={14} className="inline mr-2" /> Potvrdit platbu ({amount} Kč)
                </button>

                {order.status === "paid" && (
                    <button onClick={() => downloadReceipt(order, localStorage.getItem("strokay_name"))} data-testid="pay-download-receipt"
                        className="btn-outline w-full mt-3 flex items-center justify-center gap-2">
                        <Download size={14} /> Stáhnout účtenku (PDF)
                    </button>
                )}
            </div>
        </div>
    );
}

function CashboxBanner({ session, onOpen, onClose }) {
    if (session) {
        return (
            <div className="bg-emerald-50 border-b border-emerald-200 px-6 py-3 flex items-center justify-between text-sm" data-testid="cashbox-open">
                <span className="flex items-center gap-2 text-emerald-800"><DoorOpen size={14} /> Pokladna otevřená (počáteční stav {session.opened_cash} Kč)</span>
                <button onClick={onClose} className="text-xs uppercase tracking-[0.18em] text-emerald-900 hover:text-burgundy font-semibold" data-testid="cashbox-close-btn">
                    Zavřít pokladnu →
                </button>
            </div>
        );
    }
    return (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-center justify-between text-sm" data-testid="cashbox-closed">
            <span className="flex items-center gap-2 text-amber-800"><DoorClosed size={14} /> Pokladna je zavřená – před směnou ji otevřete zadáním počáteční hotovosti.</span>
            <button onClick={onOpen} className="text-xs uppercase tracking-[0.18em] text-amber-900 hover:text-burgundy font-semibold" data-testid="cashbox-open-btn">
                Otevřít pokladnu →
            </button>
        </div>
    );
}

function CashboxDialog({ mode, onClose, onDone, expectedInfo }) {
    const [amount, setAmount] = useState("");
    const submit = async () => {
        const val = parseInt(amount, 10);
        if (isNaN(val) || val < 0) { toast.error("Zadejte platnou částku"); return; }
        try {
            const r = mode === "open"
                ? await api.post("/cashbox/open", { opened_cash: val })
                : await api.post("/cashbox/close", { closed_cash: val });
            if (mode === "close" && r.data) {
                const { expected_cash, delta } = r.data;
                toast.success(`Uzavřeno · očekáváno ${expected_cash} Kč, rozdíl ${delta >= 0 ? "+" : ""}${delta} Kč`);
            } else {
                toast.success(mode === "open" ? "Pokladna otevřena" : "Pokladna zavřena");
            }
            onDone();
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };
    return (
        <div className="fixed inset-0 z-[60] bg-burgundyDeep/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid={`cashbox-dialog-${mode}`}>
            <div className="w-full max-w-sm bg-cream p-8 border border-ink/15">
                <p className="overline mb-2">{mode === "open" ? "Otevření pokladny" : "Uzavření pokladny"}</p>
                <h2 className="font-serif text-2xl text-ink mb-4">
                    {mode === "open" ? "Kolik je v pokladně na začátku směny?" : "Kolik je v pokladně na konci směny?"}
                </h2>
                {expectedInfo && <p className="text-xs text-ink2 mb-4">{expectedInfo}</p>}
                <input type="number" data-testid="cashbox-amount" value={amount} onChange={(e) => setAmount(e.target.value)}
                    className="w-full p-3 border border-ink/15 bg-cream2 text-lg mb-6" placeholder="0" autoFocus />
                <div className="flex gap-2">
                    <button onClick={onClose} className="btn-outline flex-1" style={{ padding: "0.7rem" }}>Zrušit</button>
                    <button onClick={submit} className="btn-burgundy flex-1" style={{ padding: "0.7rem" }} data-testid="cashbox-confirm">Potvrdit</button>
                </div>
            </div>
        </div>
    );
}

function Tutorial({ onClose }) {
    const [step, setStep] = useState(0);
    const slides = [
        { t: "Vítej v POS systému!", b: "Tady spravuješ objednávky u stolů, přidáváš vína a chuťovky a přijímáš platby." },
        { t: "Otevři si pokladnu", b: "Na začátku směny klikni Otevřít pokladnu a zadej, kolik je v ní hotovosti. Na konci ji zavři – systém spočítá, kolik má být." },
        { t: "Klikni na stůl", b: "Ťukni na stůl na mapě – otevřeš objednávku. Vyber položky z katalogu, upravuj počty +/−, přidej poznámku." },
        { t: "Zaplaceno", b: "Když host chce platit, klikni Zaplaceno. Můžeš rozdělit účet, vybrat platbu hotově / QR / kartou a zavřít stůl." },
    ];
    const s = slides[step];
    return (
        <div className="fixed inset-0 z-[70] bg-burgundyDeep/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="tutorial-modal">
            <div className="w-full max-w-md bg-cream p-8 border border-ink/15 text-center">
                <Sparkles size={26} className="text-burgundy mx-auto mb-3" />
                <p className="overline mb-2">Krok {step + 1} / {slides.length}</p>
                <h2 className="font-serif text-3xl text-ink mb-3">{s.t}</h2>
                <p className="text-sm text-ink2 mb-8 leading-relaxed">{s.b}</p>
                <div className="flex items-center justify-between gap-3">
                    <button onClick={() => { localStorage.setItem("strokay_tutorial_seen", "1"); onClose(); }} data-testid="tutorial-skip" className="text-[11px] uppercase tracking-[0.2em] text-ink2 hover:text-burgundy">Přeskočit</button>
                    {step < slides.length - 1 ? (
                        <button onClick={() => setStep(step + 1)} data-testid="tutorial-next" className="btn-burgundy" style={{ padding: "0.6rem 1.4rem" }}>Dál →</button>
                    ) : (
                        <button onClick={() => { localStorage.setItem("strokay_tutorial_seen", "1"); onClose(); }} data-testid="tutorial-finish" className="btn-burgundy" style={{ padding: "0.6rem 1.4rem" }}>Rozumím</button>
                    )}
                </div>
            </div>
        </div>
    );
}

function StaffLogin({ onLogin }) {
    const [form, setForm] = useState({ email: "", password: "" });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const r = await api.post("/auth/login", form);
            if (r.data.role !== "staff" && r.data.role !== "admin") {
                toast.error("Tento účet není brigádnický.");
                return;
            }
            localStorage.setItem("strokay_token", r.data.token);
            localStorage.setItem("strokay_role", r.data.role);
            localStorage.setItem("strokay_name", r.data.name || "");
            localStorage.setItem("strokay_color", r.data.color || "#5B7CB7");
            toast.success(`Vítej, ${r.data.name || "brigádnice"}!`);
            onLogin();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Přihlášení selhalo");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-burgundyDeep px-6">
            <form onSubmit={submit} data-testid="staff-login-form" className="w-full max-w-md bg-cream p-10 border border-ink/15">
                <Link to="/admin" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-ink2 hover:text-burgundy mb-8">
                    <ArrowLeft size={12} /> zpět
                </Link>
                <p className="overline mb-3">Brigádnice</p>
                <h1 className="font-serif text-4xl text-ink mb-8">Přihlášení</h1>
                <label className="block mb-6">
                    <span className="overline text-ink2">Uživatelské jméno</span>
                    <input data-testid="staff-login-username" className="bottom-input mt-1" value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })} required autoFocus />
                </label>
                <label className="block mb-8">
                    <span className="overline text-ink2">Heslo</span>
                    <input data-testid="staff-login-password" type="password" className="bottom-input mt-1" value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })} required />
                </label>
                <button data-testid="staff-login-submit" type="submit" className="btn-burgundy w-full" disabled={busy}>
                    {busy ? "..." : "Přihlásit se"}
                </button>
            </form>
        </div>
    );
}

function OrderPanel({ order, wines, extras, tastings, onClose, onChanged, onOpenPayment }) {
    const [items, setItems] = useState(order.items || []);
    const [note, setNote] = useState(order.note || "");
    const [category, setCategory] = useState("wine");
    const [saving, setSaving] = useState(false);

    // Sync when the order prop changes (e.g. after partial payment reload)
    useEffect(() => {
        setItems(order.items || []);
        setNote(order.note || "");
    }, [order.id, order.payments, order.subtotal]);

    const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.qty, 0), [items]);
    const paidSoFar = (order.payments || []).reduce((s, p) => s + p.amount, 0);
    const remaining = subtotal - paidSoFar;

    const catalog = useMemo(() => {
        if (category === "wine") return wines.map(w => ({ ...w, product_type: "wine", product_id: w.id }));
        if (category === "tasting") return tastings.map(t => ({
            id: t.id, name: t.name, price: t.price_per_person, product_type: "tasting", product_id: t.id
        }));
        return extras.map(e => ({ ...e, product_type: "extra", product_id: e.id }));
    }, [category, wines, tastings, extras]);

    const addItem = (p) => {
        setItems(prev => {
            const found = prev.find(i => i.product_id === p.product_id && i.product_type === p.product_type);
            if (found) return prev.map(i => i === found ? { ...i, qty: i.qty + 1 } : i);
            return [...prev, { product_type: p.product_type, product_id: p.product_id, name: p.name, price: p.price, qty: 1 }];
        });
    };
    const incQty = (idx, d) => {
        setItems(prev => prev.map((i, ix) => ix === idx ? { ...i, qty: Math.max(0, i.qty + d) } : i).filter(i => i.qty > 0));
    };
    const removeItem = (idx) => setItems(prev => prev.filter((_, ix) => ix !== idx));

    const save = useCallback(async (opts = {}) => {
        setSaving(true);
        try {
            await api.put(`/orders/${order.id}`, { items, note });
            if (opts.openPay) {
                if (!items.length) { toast.error("Prázdná objednávka"); setSaving(false); return; }
                await api.put(`/orders/${order.id}`, { items, note });
                const fresh = await api.get(`/orders/${order.id}`);
                onChanged();
                onOpenPayment(fresh.data);
            } else if (opts.close) {
                toast.success("Uloženo");
            }
            onChanged();
            if (opts.closeAfter) onClose();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    }, [order.id, items, note, onChanged, onClose, onOpenPayment]);

    const voidOrder = async () => {
        if (!window.confirm("Zrušit celou objednávku?")) return;
        await api.post(`/orders/${order.id}/void`);
        toast.success("Zrušeno");
        onChanged();
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 bg-burgundyDeep/80 backdrop-blur-sm flex items-stretch justify-end" data-testid="order-panel">
            <div className="w-full md:w-[560px] bg-cream text-ink h-full overflow-y-auto flex flex-col">
                <div className="p-6 border-b border-ink/10 flex items-center justify-between">
                    <div>
                        <p className="overline">Stůl</p>
                        <h2 className="font-serif text-3xl">{order.table_label}</h2>
                        <p className="text-xs text-ink2 mt-1">Obsluha: {order.staff_name}</p>
                    </div>
                    <button data-testid="order-close" onClick={() => save({ close: true, closeAfter: true })} className="w-10 h-10 flex items-center justify-center hover:bg-cream2">
                        <X size={18} />
                    </button>
                </div>

                {/* Category tabs */}
                <div className="p-4 border-b border-ink/10 flex gap-2">
                    {[
                        { k: "wine", l: "Vína", i: Wine },
                        { k: "tasting", l: "Degustace", i: Grape },
                        { k: "extra", l: "Ostatní", i: Coffee },
                    ].map(c => {
                        const Icon = c.i;
                        return (
                            <button key={c.k} onClick={() => setCategory(c.k)}
                                data-testid={`order-cat-${c.k}`}
                                className={`flex-1 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-[11px] uppercase tracking-[0.18em] font-semibold border transition-all ${category === c.k
                                    ? "bg-burgundy text-cream border-burgundy"
                                    : "bg-transparent text-ink border-ink/15 hover:border-burgundy"}`}>
                                <Icon size={13} /> {c.l}
                            </button>
                        );
                    })}
                </div>

                {/* Product list */}
                <div className="p-4 grid grid-cols-2 gap-2 border-b border-ink/10 max-h-[320px] overflow-y-auto">
                    {catalog.map(p => (
                        <button key={`${p.product_type}-${p.product_id}`}
                            data-testid={`order-add-${p.product_id}`}
                            onClick={() => addItem(p)}
                            className="group text-left p-3 border border-ink/10 hover:border-burgundy hover:bg-cream2 transition-all">
                            <div className="text-sm text-ink leading-tight line-clamp-2">{p.name}</div>
                            <div className="text-[11px] text-burgundy uppercase tracking-[0.18em] font-semibold mt-1">{p.price} Kč</div>
                        </button>
                    ))}
                    {catalog.length === 0 && <p className="col-span-2 text-center text-ink2 py-6 text-sm">Prázdný katalog</p>}
                </div>

                {/* Cart */}
                <div className="flex-1 overflow-y-auto p-4">
                    <p className="overline mb-3">Objednávka</p>
                    {items.length === 0 && <p className="text-ink2 text-sm">Přidejte položky z katalogu výše.</p>}
                    <ul className="space-y-2">
                        {items.map((i, idx) => (
                            <li key={idx} data-testid={`order-item-${idx}`} className="flex items-center gap-2 p-2 bg-cream2">
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-ink truncate">{i.name}</div>
                                    <div className="text-[11px] text-ink2">{i.price} Kč / ks</div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button onClick={() => incQty(idx, -1)} className="w-7 h-7 flex items-center justify-center border border-ink/15 hover:border-burgundy"><Minus size={12} /></button>
                                    <span className="w-8 text-center text-sm font-semibold" data-testid={`order-item-qty-${idx}`}>{i.qty}</span>
                                    <button onClick={() => incQty(idx, 1)} className="w-7 h-7 flex items-center justify-center border border-ink/15 hover:border-burgundy"><Plus size={12} /></button>
                                    <button onClick={() => removeItem(idx)} className="w-7 h-7 flex items-center justify-center text-ink2 hover:text-burgundy"><X size={12} /></button>
                                </div>
                                <div className="w-16 text-right text-sm font-semibold shrink-0">{i.price * i.qty} Kč</div>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-6">
                        <label className="block">
                            <span className="overline flex items-center gap-2"><StickyNote size={11} /> Poznámka</span>
                            <textarea data-testid="order-note" value={note} onChange={(e) => setNote(e.target.value)}
                                className="w-full mt-2 p-2 border border-ink/15 bg-cream text-sm resize-none" rows={2}
                                placeholder="např. bez ledu, extra sklo…" />
                        </label>
                    </div>
                </div>

                {/* Footer */}
                <div className="border-t border-ink/10 p-4 bg-cream2 sticky bottom-0">
                    <div className="flex items-center justify-between mb-1">
                        <span className="overline">Celkem</span>
                        <span data-testid="order-subtotal" className="font-serif text-3xl text-ink">{subtotal} Kč</span>
                    </div>
                    {paidSoFar > 0 && <div className="flex items-center justify-between mb-3 text-xs text-emerald-800"><span>Zaplaceno {paidSoFar} Kč · zbývá</span><span className="font-semibold">{remaining} Kč</span></div>}
                    <div className="flex gap-2 mt-2">
                        <button onClick={voidOrder} data-testid="order-void" className="btn-outline flex-1" style={{ padding: "0.7rem" }}>Zrušit</button>
                        <button onClick={() => save({ close: true, closeAfter: true })} disabled={saving} data-testid="order-save"
                            className="btn-outline flex-1" style={{ padding: "0.7rem" }}>Uložit</button>
                        <button onClick={() => save({ openPay: true })} disabled={saving || !items.length} data-testid="order-pay"
                            className="btn-burgundy flex-1" style={{ padding: "0.7rem" }}>
                            <Check size={13} className="inline mr-1" /> Zaplaceno
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function TableMap({ tables, orders, wines, extras, tastings, onOpenOrder, onChanged, activeOrder, setActiveOrder }) {
    const openOrders = useMemo(() => {
        const map = {};
        for (const o of orders) if (o.status === "open") map[o.table_id] = o;
        return map;
    }, [orders]);

    return (
        <div className="p-6 md:p-10 flex-1">
            <div className="mb-6">
                <p className="overline">Mapa stolů</p>
                <h2 className="font-serif text-4xl text-ink">Vyberte stůl</h2>
                <p className="text-sm text-ink2 mt-2">Zelený puntík = otevřený stůl. Kliknutím na stůl otevřete nebo pokračujete v objednávce.</p>
            </div>

            <div className="relative bg-gradient-to-br from-[#EDEBE6] to-[#DED8CB] border border-ink/15 aspect-[16/9] max-w-5xl mx-auto overflow-hidden">
                {/* decorative sklep marker */}
                <div className="absolute top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.28em] text-ink/50">Sklep</div>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.28em] text-ink/40">Ulička</div>

                {tables.map(t => {
                    const isOpen = !!openOrders[t.id];
                    const order = openOrders[t.id];
                    return (
                        <button key={t.id} data-testid={`table-${t.label}`}
                            onClick={async () => {
                                if (order) { setActiveOrder(order); return; }
                                const r = await api.post("/orders", { table_id: t.id });
                                setActiveOrder(r.data);
                                onChanged();
                            }}
                            className={`absolute -translate-x-1/2 -translate-y-1/2 w-14 h-14 md:w-24 md:h-24 rounded-full flex flex-col items-center justify-center font-serif transition-all duration-200 hover:scale-110 border-2 ${isOpen
                                ? "bg-burgundy text-cream border-burgundy shadow-[0_0_0_4px_rgba(30,43,77,0.15)] md:shadow-[0_0_0_6px_rgba(30,43,77,0.15)]"
                                : "bg-cream text-ink border-ink/25 hover:border-burgundy"}`}
                            style={{ left: `${t.x}%`, top: `${t.y}%` }}>
                            <span className="text-sm md:text-2xl leading-none">{t.label}</span>
                            <span className="text-[8px] md:text-[10px] uppercase tracking-widest mt-0.5 md:mt-1 opacity-80">
                                <span className="md:hidden">{t.seats}m</span>
                                <span className="hidden md:inline">{t.seats} míst</span>
                            </span>
                            {isOpen && (
                                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 md:w-3 md:h-3 rounded-full bg-emerald-400 border-2 border-cream" />
                            )}
                            {isOpen && order && (
                                <span className="absolute -bottom-5 md:-bottom-6 text-[9px] md:text-[10px] uppercase tracking-wider text-burgundy font-semibold whitespace-nowrap">
                                    {order.subtotal} Kč
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Open orders list */}
            {Object.keys(openOrders).length > 0 && (
                <div className="mt-12 max-w-5xl mx-auto">
                    <p className="overline mb-4">Otevřené stoly ({Object.keys(openOrders).length})</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {Object.values(openOrders).map(o => (
                            <button key={o.id} onClick={() => setActiveOrder(o)}
                                data-testid={`open-order-${o.table_label}`}
                                className="p-4 bg-cream border border-ink/10 hover:border-burgundy text-left transition-colors">
                                <div className="flex items-baseline justify-between mb-1">
                                    <span className="font-serif text-xl text-ink">Stůl {o.table_label}</span>
                                    <span className="text-sm font-semibold text-burgundy">{o.subtotal} Kč</span>
                                </div>
                                <div className="text-[11px] uppercase tracking-[0.18em] text-ink2">{o.staff_name} · {o.items?.length || 0} pol.</div>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function StaffPage() {
    const nav = useNavigate();
    const [authed, setAuthed] = useState(!!localStorage.getItem("strokay_token"));
    const [tables, setTables] = useState([]);
    const [orders, setOrders] = useState([]);
    const [wines, setWines] = useState([]);
    const [extras, setExtras] = useState([]);
    const [tastings, setTastings] = useState([]);
    const [activeOrder, setActiveOrder] = useState(null);
    const [paymentOrder, setPaymentOrder] = useState(null);
    const [name, setName] = useState(localStorage.getItem("strokay_name") || "");
    const [cashbox, setCashbox] = useState(null);
    const [cashDialog, setCashDialog] = useState(null); // "open" | "close" | null
    const [showTutorial, setShowTutorial] = useState(false);

    const load = useCallback(async () => {
        try {
            const [t, o, w, e, ts, cb] = await Promise.all([
                api.get("/tables"),
                api.get("/orders/open"),
                api.get("/wines"),
                api.get("/extras"),
                api.get("/tastings"),
                api.get("/cashbox/current"),
            ]);
            setTables(t.data);
            setOrders(o.data);
            setWines(w.data);
            setExtras(e.data);
            setTastings(ts.data);
            setCashbox(cb.data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.clear();
                setAuthed(false);
            }
        }
    }, []);

    useEffect(() => {
        if (!authed) return;
        load();
        const id = setInterval(load, 15000);
        return () => clearInterval(id);
    }, [authed, load]);

    useEffect(() => {
        if (authed && !localStorage.getItem("strokay_tutorial_seen")) setShowTutorial(true);
    }, [authed]);

    const logout = () => {
        localStorage.removeItem("strokay_token");
        localStorage.removeItem("strokay_role");
        localStorage.removeItem("strokay_name");
        localStorage.removeItem("strokay_color");
        setAuthed(false);
        nav("/admin");
    };

    if (!authed) return <StaffLogin onLogin={() => { setAuthed(true); setName(localStorage.getItem("strokay_name") || ""); }} />;

    return (
        <div className="min-h-screen bg-cream2 flex flex-col">
            <header className="bg-cream border-b border-ink/15 px-6 md:px-10 py-4 flex items-center justify-between">
                <div className="flex items-baseline gap-3">
                    <Link to="/" className="font-serif text-2xl text-ink">Štrokay</Link>
                    <span className="overline">POS · Obsluha</span>
                </div>
                <div className="flex items-center gap-4">
                    <span className="text-sm text-ink2">Přihlášen: <b className="text-ink">{name}</b></span>
                    <button onClick={() => setShowTutorial(true)} className="text-xs uppercase tracking-[0.2em] text-ink2 hover:text-burgundy" data-testid="staff-tutorial-btn">Návod</button>
                    <button data-testid="staff-logout" onClick={logout} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink2 hover:text-burgundy">
                        <LogOut size={14} /> Odhlásit
                    </button>
                </div>
            </header>

            <CashboxBanner
                session={cashbox}
                onOpen={() => setCashDialog("open")}
                onClose={() => setCashDialog("close")}
            />

            <TableMap
                tables={tables}
                orders={orders}
                wines={wines}
                extras={extras}
                tastings={tastings}
                onChanged={load}
                activeOrder={activeOrder}
                setActiveOrder={setActiveOrder}
            />

            {activeOrder && (
                <OrderPanel
                    order={activeOrder}
                    wines={wines}
                    extras={extras}
                    tastings={tastings}
                    onClose={() => { setActiveOrder(null); load(); }}
                    onChanged={load}
                    onOpenPayment={(o) => { setActiveOrder(null); setPaymentOrder(o); }}
                />
            )}

            {paymentOrder && (
                <PaymentModal
                    order={paymentOrder}
                    onClose={() => {
                        // If partial payment left order open, reopen OrderPanel with the updated order
                        const wasPartial = paymentOrder.status === "open";
                        setPaymentOrder(null);
                        if (wasPartial) setActiveOrder(paymentOrder);
                        load();
                    }}
                    onPaid={(updated, isFullyPaid) => {
                        load();
                        if (isFullyPaid) {
                            // Keep modal open with paid order so staff can download receipt
                            setPaymentOrder(updated);
                        }
                        else setPaymentOrder(updated);
                    }}
                />
            )}

            {cashDialog && (
                <CashboxDialog
                    mode={cashDialog}
                    onClose={() => setCashDialog(null)}
                    onDone={() => { setCashDialog(null); load(); }}
                    expectedInfo={cashDialog === "close" ? "Systém spočítá očekávanou částku podle přijatých hotovostních plateb od otevření pokladny." : null}
                />
            )}

            {showTutorial && <Tutorial onClose={() => setShowTutorial(false)} />}
        </div>
    );
}
